import { useCallback, useMemo, useState } from "react";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Shield2Icon } from "@/shared/icons";
import { SoundButton } from "@/shared/ui";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  inventoryByIdSelector,
  inventoryMapSelector,
  setMemoizedItem,
} from "@/domains/inventory/slice";
import getDisabledItemsByFilters from "@/domains/inventory/helpers/getDisabledItemsByFilters";
import { placeItemsWithMemo } from "@/domains/inventory/helpers/placeItemsWithMemo";
import useSearchManager from "@/domains/inventory/hooks/search/useSearchManager";
import useFilterItems from "@/domains/inventory/hooks/useFilterItems";
import useSearchItems from "@/domains/inventory/hooks/useSearchItems";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryFilter from "@/domains/inventory/components/InventoryFilter";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventorySearch from "@/domains/inventory/components/InventorySearch";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";
import InventorySearchConfiscationHeader from "@/domains/inventory/components/search/InventorySearchConfiscationHeader";

type Props = { inventoryId: number };

export default function InventorySearchConfiscationContainer({ inventoryId }: Props) {
  const [isConfiscation, setIsConfiscation] = useState(false);
  const dispatch = useAppDispatch();
  const inventory = useAppSelector((state) => inventoryByIdSelector(state, inventoryId));
  const inventoryMap = useAppSelector(inventoryMapSelector);
  const characterInventory = useMemo(() => {
    let result = null;
    Object.keys(inventoryMap).forEach((key) => {
      if (inventoryMap[key].ownerType === OwnerType.Character) {
        result = inventoryMap[key];
        return;
      }
    });

    return result;
  }, [inventoryMap]);
  const { addItemToConfiscation, confiscate } = useSearchManager();

  const itemsWithMemo = useMemo(() => {
    if (inventory) {
      return placeItemsWithMemo(inventory.items, inventory.size.width, inventory.size.height);
    }
    return [];
  }, [inventory]);
  const { filteredItems, filterGroups, selectedGroups, onSelectHandler } =
    useFilterItems(itemsWithMemo);
  const { onChangeSearchValue, deferredSearchValue, itemsBySearch } = useSearchItems(filteredItems);

  const onConfiscation = useCallback(async () => {
    if (inventory && characterInventory) {
      try {
        setIsConfiscation(true);
        await confiscate({
          inventoryItemIds: inventory.items.map((item) => item.id),
          inventoryId: characterInventory.id, // передаем id инвентаря игрока, которого обыскиваем
        });
        GameMpProvider.closeCurrentInterface();
      } catch (e) {
        console.log(e);
      } finally {
        setIsConfiscation(false);
      }
    }
  }, [inventory, confiscate, characterInventory]);

  const disabledItems = useMemo(() => {
    if (inventory) {
      return getDisabledItemsByFilters(inventory.items, itemsBySearch);
    }
    return [];
  }, [inventory, itemsBySearch]);

  const onCloseHandler = useCallback(() => {
    GameMpProvider.closeCurrentInterface();
  }, []);

  if (!inventory) return null;

  return (
    <InventoryWrapper className="max-h-[31.875rem]">
      <InventoryHeader className="px-0 border border-b-0 border-dark-900 rounded-t-[inherit] overflow-clip">
        <InventorySearchConfiscationHeader onClose={onCloseHandler} />
      </InventoryHeader>
      <InventoryContent className="flex items-center justify-between w-full h-[3rem]">
        <InventorySearch
          value={deferredSearchValue}
          onChange={onChangeSearchValue}
          className="w-fit"
        />
        {filterGroups.length > 0 && (
          <InventoryFilter
            groups={filterGroups}
            selectedGroups={selectedGroups}
            onSelect={onSelectHandler}
          />
        )}
      </InventoryContent>
      <InventoryContent className="flex-1 overflow-clip overflow-y-auto pb-6">
        <InventoryGrid
          inventoryId={inventory.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          disabledItems={disabledItems}
          items={inventory.items}
          onAddItemToConfiscation={addItemToConfiscation}
          onMemoizedItem={(item) => dispatch(setMemoizedItem(item))}
        />
      </InventoryContent>
      <InventoryFooter>
        {/* TODO: написать функцию для подсчета веса */}
        <InventoryWeight weight={inventory.weight} />
        <SoundButton
          className="h-[3rem] flex items-center gap-2.5 text-14 font-bold capitalize"
          onClick={onConfiscation}
          disabled={isConfiscation}
          loading={isConfiscation}
        >
          <Shield2Icon size={20} />
          Конфисковать
        </SoundButton>
      </InventoryFooter>
    </InventoryWrapper>
  );
}
